import React, { useEffect, useState } from 'react';
import { openBesideChat } from 'shared/components/records/recordsSurface';
import { ExternalLink, Rocket, X, Wrench } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import { getProposal, approveProposal, rejectProposal } from 'features/evals3/api/evals3Api';
import { TargetPill } from 'features/evals3/components/EvalsTable';
import ProposalGraph from 'features/evals3/editor/ProposalGraph';

/**
 * A change Ask proposed to an eval (the `proposal` answer block).
 *
 * The same object the Proposed changes page shows: a new version, NOT in
 * production, with why, which steps change, what it does to the results in
 * question, what else it would flip, whether it breaks anything a person
 * labelled — and what to fix in the agent when the agent, not the eval, is
 * at fault. Put in production moves the label; Reject records why. The card
 * reads the proposal's state on mount, so a reopened thread is current.
 */

const STATUS_WORD = {
  approved: 'In production', rejected: 'Rejected', superseded: 'Superseded', auto_promoted: 'In production', needs_human: 'Needs a person',
};

function Fact({ children, tone = 'muted' }) {
  const color = tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : 'text-[color:var(--text-secondary)]';
  return <div className={`text-[12px] ${color}`}>{children}</div>;
}

export default function ProposalCard({ block }) {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;
  const teamSlug = selectedTeam?.slug || selectedTeam?.name || (teamId != null ? String(teamId) : '');
  const [status, setStatus] = useState('open');
  const [live, setLive] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let off = false;
    if (!teamId || !block?.proposal_id) return undefined;
    getProposal(teamId, block.proposal_id)
      .then((d) => { if (!off && d?.proposal) { setStatus(d.proposal.status); setLive(d.proposal); } })
      .catch(() => {});
    return () => { off = true; };
  }, [teamId, block?.proposal_id]);

  if (!block || !block.proposal_id) return null;
  const e = block.eval || {};
  const t = block.tried;
  const pv = block.preview;
  const rg = block.regression;
  const s = block.suggestion;
  const open = status === 'open';
  const changes = Array.isArray(block.changes) ? block.changes : [];

  const putLive = async () => {
    setBusy(true); setError(null);
    try {
      const out = await approveProposal(teamId, block.proposal_id);
      setStatus(out?.proposal?.status || 'approved');
      setLive(out?.proposal || live);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message);
    } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!reason.trim()) { setError('Say why: a rejection teaches the next proposal.'); return; }
    setBusy(true); setError(null);
    try {
      await rejectProposal(teamId, block.proposal_id, reason.trim());
      setStatus('rejected'); setRejecting(false);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message);
    } finally { setBusy(false); }
  };
  const goProposals = () => openBesideChat(`/${encodeURIComponent(teamSlug)}/agent-analytics/evals?mode=proposals&eval=${encodeURIComponent(e.id)}`);

  return (
    <div data-testid="proposal-card" className="trodo-answer-block my-3 rounded-lg border border-[color:var(--border-default)] bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Proposed change</div>
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate text-[14px] font-semibold text-[color:var(--text-primary)]">{e.name}</span>
            {e.target && <TargetPill target={e.target} />}
            {!block.eval_was_right && block.version && (
              <span className="text-[12px] text-[color:var(--text-muted)]">v{block.from_version} → v{block.version}</span>
            )}
          </div>
        </div>
        <span data-testid="proposal-status" className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${open ? 'bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)]' : status === 'approved' || status === 'auto_promoted' ? 'bg-success-soft text-success' : 'bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)]'}`}>
          {open ? 'Not in production' : STATUS_WORD[status] || status}
        </span>
      </div>

      {block.rationale && <p className="mt-2 text-[13px] leading-relaxed text-[color:var(--text-secondary)]">{block.rationale}</p>}

      {block.eval_was_right ? (
        <div className="mt-2 text-[12px] text-[color:var(--text-muted)]">No change to the eval: the results show the agent went wrong, and the eval caught it.</div>
      ) : (
        <>
          {changes.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-[color:var(--text-muted)]">
              Changes:
              {changes.map((c) => (
                <span key={c.id} className="rounded bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[color:var(--text-secondary)]">
                  {c.name}{c.change === 'added' ? ' (new)' : c.change === 'removed' ? ' (removed)' : ''}
                </span>
              ))}
            </div>
          )}
          {block.graph && (
            <div className="mt-2">
              <ProposalGraph base={block.base_graph} proposed={block.graph} target={block.target_step} height={Math.min(380, 170 + 28 * Object.keys(block.graph.nodes || {}).length)} />
            </div>
          )}
          <div className="mt-2 grid gap-1 rounded-md bg-[color:var(--surface-sunken)] px-3 py-2" data-testid="proposal-facts">
            {t && t.tried > 0 && (
              <Fact tone={t.changed === 0 ? 'bad' : 'good'}>
                {t.changed === 0
                  ? `Tried on ${t.tried} of the results in question: none come out differently`
                  : `Tried on ${t.tried} of the results in question: ${t.changed} now ${t.changed === 1 ? 'comes' : 'come'} out differently (${t.now_pass} pass, ${t.now_fail} fail)`}
                {t.labelled ? ` · against what people said: fixes ${t.fixed}${t.still_wrong ? `, ${t.still_wrong} still wrong` : ''}` : ''}
                {t.errored ? ` · ${t.errored} errored` : ''}
              </Fact>
            )}
            {pv && !pv.refused && (
              <Fact>Recent results it would change: {pv.changed} of {pv.total}{pv.changed ? ` (${pv.fail_to_pass} fail → pass, ${pv.pass_to_fail} pass → fail)` : ''}</Fact>
            )}
            {pv && pv.refused && <Fact>It changes a judge, so recent results cannot be replayed without calling it.</Fact>}
            {rg && rg.gate === 'replayed' && (
              <Fact tone={rg.flipped ? 'bad' : 'good'}>
                {rg.checked ? `Results a person labelled: ${rg.flipped ? `breaks ${rg.flipped}` : 'breaks none'} of ${rg.checked}${rg.fixed ? ` · fixes ${rg.fixed}` : ''}` : 'No results a person labelled to check it against.'}
              </Fact>
            )}
            {rg && rg.gate === 'not_replayable' && <Fact>A judge changes, so it is not checked against labelled results.</Fact>}
          </div>
          {block.blocked && <div className="mt-2 text-[12px] text-danger">The new version could not be written: {block.blocked}</div>}
        </>
      )}

      {s && (
        <div className="mt-2 rounded-md border border-[color:var(--border-subtle)] px-3 py-2" data-testid="proposal-agent">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            <Wrench size={11} aria-hidden /> What to fix in the agent{s.agent_part ? ` · ${s.agent_part}` : ''}
          </div>
          <div className="mt-1 text-[13px] font-medium text-[color:var(--text-primary)]">{s.title}</div>
          <p className="mt-0.5 text-[12px] leading-relaxed text-[color:var(--text-secondary)]">{s.body}</p>
          {s.change && <p className="mt-1 text-[12px] leading-relaxed text-[color:var(--text-primary)]"><span className="text-[color:var(--text-muted)]">Change: </span>{s.change}</p>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {open && !block.eval_was_right && block.version && !rejecting && (
          <button type="button" onClick={putLive} disabled={busy || !teamId}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:opacity-60">
            <Rocket size={13} aria-hidden /> Put in production
          </button>
        )}
        {open && !rejecting && (
          <button type="button" onClick={() => setRejecting(true)} disabled={busy}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover">
            <X size={13} aria-hidden /> Reject
          </button>
        )}
        {rejecting && (
          <div className="flex w-full flex-wrap items-center gap-2">
            <input autoFocus value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Why not? It teaches the next proposal."
              aria-label="Why reject" className="input h-8 min-w-[220px] flex-1 text-[12px]" />
            <button type="button" onClick={reject} disabled={busy} className="inline-flex h-8 items-center rounded-md bg-[color:var(--feedback-error)] px-3 text-[12px] font-medium text-white disabled:opacity-60">Reject</button>
            <button type="button" onClick={() => { setRejecting(false); setError(null); }} className="text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Cancel</button>
          </div>
        )}
        {!rejecting && (
          <button type="button" onClick={goProposals}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover">
            Open in Proposed changes <ExternalLink size={12} aria-hidden />
          </button>
        )}
        {!open && live?.decision_reason && status === 'rejected' && <span className="text-[12px] text-[color:var(--text-muted)]">“{live.decision_reason}”</span>}
        {error && <span className="text-[12px] text-danger" role="alert">{error}</span>}
      </div>
    </div>
  );
}
